/**
 * Das grosse Tiefenprofil im Detail.
 *
 * uPlot, weil es fuer genau diesen Fall gebaut ist: mehrere tausend Punkte,
 * ohne dass ein Diagrammrahmenwerk mitgeliefert werden muss. Die Y-Achse ist
 * umgekehrt — Tiefe waechst nach unten, wie auf jedem Tauchcomputer.
 */

import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';
import { tiefenfarbe } from './tiefenfarbe.js';

const FELD = { t: 0, tiefe: 1, temp: 2, hf: 3, druck: 4, deko: 5, ndl: 6 };

/**
 * Die Reihen fuer das Diagramm — Zeit, Dekodecke, Tiefe, Temperatur.
 *
 * Die Dekodecke steht **vor** der Tiefe, weil uPlot in dieser Reihenfolge
 * zeichnet: Sie soll unter der Kurve liegen, nicht darueber.
 *
 * Sie wird nicht gerechnet, sondern abgelesen. Der Tauchcomputer hat sie
 * waehrend des Tauchgangs gesetzt und die Pipeline hat sie als
 * `deco_depth_m` mitgeschrieben; dieses Programm rechnet keine Dekompression
 * (siehe „Nicht bauen") und faengt hier auch nicht damit an. Gezeigt wird,
 * was aufgezeichnet wurde.
 *
 * Null und Nullkomma sind keine Decke, sondern ihr Fehlen: An der Oberflaeche
 * steht dort eine 0, und eine Flaeche von 0 bis 0 waere ein Strich am oberen
 * Rand ueber die ganze Breite.
 */
export function reihen(messpunkte) {
  const zeit = [];
  const tiefe = [];
  const temperatur = [];
  const deko = [];
  for (const punkt of messpunkte) {
    zeit.push(punkt[FELD.t] / 60);          // Minuten lesen sich besser als Sekunden
    tiefe.push(punkt[FELD.tiefe] ?? null);
    temperatur.push(punkt[FELD.temp] ?? null);
    const decke = punkt[FELD.deko];
    deko.push(typeof decke === 'number' && decke > 0 ? decke : null);
  }
  return [zeit, deko, tiefe, temperatur];
}

/** Hat der Tauchgang ueberhaupt eine Dekodecke aufgezeichnet? */
export function hatDeko(reihe) {
  return reihe.some((wert) => wert !== null);
}

/**
 * Wie hoch das Profil sein darf.
 *
 * Der feste Sockel von 240 Pixeln stammt vom Schreibtisch, wo er ein Fünftel
 * des Fensters ausmacht. Auf einem Telefon sind es 36 Prozent des Schirms —
 * und darunter liegt das ganze Datenblatt, an dem man jedes Mal vorbeiscrollen
 * muss. Unter 500 Pixel Breite reicht ein niedrigerer Sockel: Die Form der
 * Kurve bleibt lesbar, sie ist ja breiter als hoch.
 *
 * Gerechnet wird aus der Breite des Behälters, nicht aus der des Fensters —
 * dasselbe Diagramm in einer schmalen Spalte soll sich gleich verhalten.
 */
function hoeheFuer(breite) {
  const sockel = breite < 500 ? 180 : 240;
  return Math.min(420, Math.max(sockel, Math.round(breite * 0.42)));
}

export function zeichneProfil(behaelter, messpunkte, maxTiefe) {
  const daten = reihen(messpunkte);
  const stil = getComputedStyle(document.documentElement);
  const raster = stil.getPropertyValue('--linie').trim() || 'rgba(255,255,255,0.08)';
  const schrift = stil.getPropertyValue('--text-leise').trim() || '#8aa0b4';

  const optionen = {
    width: behaelter.clientWidth,
    height: hoeheFuer(behaelter.clientWidth),
    padding: [12, 16, 8, 8],
    cursor: { y: false, points: { size: 6 } },
    legend: { show: false },
    scales: {
      x: { time: false },
      tiefe: { dir: -1, range: (u, min, max) => [0, Math.max(max, maxTiefe || 0) * 1.05] },
      temp: { auto: true },
    },
    axes: [
      { stroke: schrift, grid: { stroke: raster, width: 1 }, ticks: { stroke: raster },
        values: (u, werte) => werte.map((w) => `${w} min`) },
      { scale: 'tiefe', stroke: schrift, grid: { stroke: raster, width: 1 },
        ticks: { stroke: raster }, values: (u, werte) => werte.map((w) => `${w} m`) },
      { scale: 'temp', side: 1, stroke: schrift, grid: { show: false },
        values: (u, werte) => werte.map((w) => `${w}°`) },
    ],
    series: [
      {},
      {
        // Der Bereich zwischen Oberflaeche und Decke: dorthin darf man nicht
        // auftauchen. Gefuellt von 0 bis zur Decke, weil die Flaeche die
        // Aussage ist — eine blosse Linie liest sich wie eine zweite Kurve.
        label: 'Dekostopp',
        scale: 'tiefe',
        width: 1,
        stroke: 'rgba(232, 122, 112, 0.95)',
        dash: [5, 3],
        fill: 'rgba(224, 108, 98, 0.26)',
        spanGaps: false,
      },
      {
        label: 'Tiefe',
        scale: 'tiefe',
        width: 2,
        stroke: (u) => {
          const verlauf = u.ctx.createLinearGradient(0, u.bbox.top, 0, u.bbox.top + u.bbox.height);
          for (let i = 0; i <= 6; i += 1) {
            verlauf.addColorStop(i / 6, tiefenfarbe(((maxTiefe || 40) * i) / 6));
          }
          return verlauf;
        },
        fill: (u) => {
          const verlauf = u.ctx.createLinearGradient(0, u.bbox.top, 0, u.bbox.top + u.bbox.height);
          verlauf.addColorStop(0, 'rgba(244, 182, 106, 0.16)');
          verlauf.addColorStop(1, 'rgba(52, 62, 128, 0.05)');
          return verlauf;
        },
      },
      { label: 'Temperatur', scale: 'temp', width: 1, stroke: 'rgba(214, 156, 94, 0.55)',
        dash: [4, 4] },
    ],
  };
  const diagramm = new uPlot(optionen, daten, behaelter);

  /**
   * Die Breite wird beobachtet, nicht einmal gemessen.
   *
   * `resize` am Fenster reichte, solange die Ansicht beim Zeichnen schon im
   * Dokument stand. Seit die Ansichten mit einem Übergang eingewechselt werden,
   * ist der Behälter im Augenblick des Zeichnens noch nicht angehängt —
   * `clientWidth` ist dann 0, und das Diagramm bliebe für immer null Pixel
   * breit. Ein `ResizeObserver` meldet die erste echte Breite von selbst und
   * deckt nebenbei den Fall mit ab, für den `resize` gedacht war: das gedrehte
   * Telefon.
   */
  const anpassen = () => {
    const breite = behaelter.clientWidth;
    if (breite <= 0) return;        // noch nicht im Layout — die nächste Meldung kommt
    diagramm.setSize({ width: breite, height: hoeheFuer(breite) });
  };

  const beobachter = new ResizeObserver(anpassen);
  beobachter.observe(behaelter);
  return { diagramm, daten, aufraeumen: () => beobachter.disconnect() };
}
